import { Component, ElementRef, EventEmitter, inject, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Moment } from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, concat, firstValueFrom, merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, filter, first, map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { EmployerObjectDictionaryDto } from 'src/app/models/dtos/employer-object-dictionary-dto';
import { EmploymentAgreementDto } from 'src/app/models/dtos/employment-agreement-dto';
import { AlertDialogComponent } from 'src/app/shared/messages/alert-dialog/alert-dialog.component';
import { TreeNode } from 'src/app/shared/models/tree-node';
import { AgreementTypeDto } from 'src/app/models/dtos/agreement-type-dto';
import { SupervisorsService } from '../data/supervisors.service';
import { SupervisorDto } from '../models/supervisor.dto';
import { AgreementTypeEnum } from '../models/enums/worker-agreement-type-enum';
import { BasicInformationStepFormGroupService } from '../agreement-of-employment/services/basic-information-step-form-group.service';
import moment from 'moment';
import { AuthService } from '../core/authentication/auth.service';
import { Permission } from '../common/enums/permissions';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TreeViewerComponent, TreeViewerComponentData } from '../shared/components/tree-viewer/tree-viewer.component';
import { EmploymentType } from '../models/enums/employment-type-enum';
import { CompanyDto } from '../models/dtos/CompanyDto';
import { NewContractDurationUnit } from '../models/enums/new-contract-duration-unit.enum';
import { arrayComparer } from '../common/comparators/arrayComparer';
import { AvailableOrderItemsDto } from '../models/dtos/orders/available-order-items-dto';
import { OrderService } from '../data/order.service';
import { WorkerService } from '../data/worker.service';
import { WorkerAgreementService } from '../data/worker-agreement.service';
import { LegalizationService } from '../data/legalization.service';
import { SubmitForMultipleLegalizationsDto } from '../models/dtos/submit-for-legalization-multiple-dto';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { EmployerService } from '../data/employer.service';
import { Responsibility } from '../models/responsibility';
import { PotentialSupervisorDto } from '../models/dtos/supervisors/potential-supervisor-dto';
import { ModuleName, ModulePermissionService } from '../subscription-package';
import { isPlannedPeriodWithinThreshold } from 'src/app/common/utils/planned-period.utils';

type EmployerFormModel = { Id: number, Name?: string | null }

function isEmployer(value: unknown): value is EmployerFormModel {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const employer = value as { Id?: unknown; Name?: unknown };
  return typeof employer.Id === 'number'
    && (typeof employer.Name === 'string' || employer.Name == null);
}

@Component({
  template: '',
  standalone: false,
})
export abstract class BasicInformationBaseComponent {
  private readonly defaultNextContractDurationValueMin = 1;
  private readonly defaultNextContractDurationValueMax = 33;

  protected route = inject(ActivatedRoute);
  protected dictionaryService = inject(DictionaryService);
  protected supervisorsService = inject(SupervisorsService);
  protected dialog = inject(MatDialog);
  protected spinner = inject(NgxSpinnerService);
  protected router = inject(Router);
  protected basicInformationStepFormGroupService = inject(BasicInformationStepFormGroupService);
  protected authService = inject(AuthService);
  protected workerService = inject(WorkerService);
  protected workerAgreementService = inject(WorkerAgreementService);
  protected legalizationService = inject(LegalizationService);
  protected snackbarService = inject(SnackBarService);
  protected orderService = inject(OrderService);
  protected employerService = inject(EmployerService);
  protected modulePermissionService = inject(ModulePermissionService);

  public listOfCompanies$: Observable<CompanyDto[]> = this.dictionaryService.getCompaniesList();
  public listOfEmployers$: Observable<DictionaryItem[]> = this.dictionaryService.getEmployersList();

  public organizationalUnitsSubject$ = new BehaviorSubject<number>(null);
  listOfOrganizationalUnits$: Observable<TreeNode[]> = this.organizationalUnitsSubject$.asObservable().pipe(
    switchMap((organizationalUnitId: number) => {
      return this.dictionaryService
        .getOrganizationalUnits(organizationalUnitId, this.employmentTypeId)
        .pipe(map((arr) => arr.map((ou) => <any>{ ...ou, Checked: false, Expanded: false })));
    }),
  );

  public listOfResponsibilities$: Observable<Responsibility[]>;
  public listOfAgreementTypes$: Observable<AgreementTypeDto[]> = of([]);
  public listOfTemporaryWorkplaces$: Observable<DictionaryItem[]> = of([]);
  public listOfEmployerObjects$: Observable<EmployerObjectDictionaryDto[]> = of([]);
  public isEmployerObjectsLoading = false;
  public listOfLocations$: Observable<DictionaryItem[]> = of([]);
  public listOfSupervisors$: Observable<SupervisorDto[]> = of([]);
  public listOfJobTitles$: Observable<DictionaryItem[]> = of([]);
  public listOfAvailableOrderItems$: Observable<AvailableOrderItemsDto[]> = null;
  public selectedEmployerObject: EmployerObjectDictionaryDto | null = null;
  public readonly moduleNames = ModuleName;
  public filteredJobTitles$: Observable<DictionaryItem[]>;
  public listOfSupervisorJobTitles$: Observable<DictionaryItem[]> = of([]);
  public filteredSupervisorJobTitles$: Observable<DictionaryItem[]>;


  protected readonly minimumInputLetters = 2;
  protected readonly timeBetweenInput = 300;
  protected readonly permittedNumberOfDays = 540;
  protected readonly maxDaysBefore = 7;
  public readonly contractDurationUnits = Object.values(NewContractDurationUnit)
    .filter((value): value is NewContractDurationUnit => typeof value === 'number')
    .sort((left, right) => left - right);
  public nextContractDurationValueMin = this.defaultNextContractDurationValueMin;
  public nextContractDurationValueMax = this.defaultNextContractDurationValueMax;
  protected readonly unsubscribe$ = new Subject<void>();

  private readonly daysToSubtractFromMaxDate = 1;

  protected employmentTypeId: number;
  protected agreementId: number;
  protected canBeConcludedForAnIndefinitePeriod: boolean;
  protected hasMinimalWageValidation: boolean;
  protected maxDurationMonths: number | null;
  protected isSelectedAgreementTypeTrial = false;
  protected isPlannedPeriodOptionEnabled = false;
  protected orderItemLoaded: boolean;
  orderItemActive: boolean;
  protected selectedCompany: CompanyDto;
  private supervisorModuleAllowed = false;

  protected filterSubject = new Subject<string>();
  private readonly refreshResponsibilities$ = new Subject<void>();
  protected filteredResponsibilities$: Observable<Responsibility[]>;
  public jobTitleFilterCtrl = new UntypedFormControl('');
  public supervisorJobTitleFilterCtrl = new UntypedFormControl('');

  hasAccessToSupervisorJobTitle$ = this.workerAgreementService.hasAccessToSupervisorJobTitle();

  @Input() prefetchedAgreement: EmploymentAgreementDto;
  @Input() basicInformationStepFormGroup: UntypedFormGroup;
  @Input() workerId: number;
  @Output() formGroupChange: EventEmitter<EmploymentAgreementDto> = new EventEmitter();
  @Output() basicInformationSaved: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('employerObjectInput') protected employerObjectSelectRef: ElementRef;
  @ViewChild('employerObjectInput', { read: MatAutocompleteTrigger }) employerObjectSelect: MatAutocompleteTrigger;

  constructor() {
    this.employmentTypeId = this.route.snapshot.params.employmentTypeId;
    this.agreementId = this.route.snapshot.params.agreementId;
    this.listOfAgreementTypes$ = this.dictionaryService.getAgreementTypes([this.employmentTypeId]);
  }
  get agreementType() {
    return this.basicInformationStepFormGroup.get('agreementType') as UntypedFormControl;
  }
  get mpk() {
    return this.basicInformationStepFormGroup.get('mpk') as UntypedFormControl;
  }
  get temporaryWorkplace() {
    return this.basicInformationStepFormGroup.get('temporaryWorkplace') as UntypedFormControl;
  }
  get supervisor() {
    return this.basicInformationStepFormGroup.get('supervisor') as UntypedFormControl;
  }
  get location() {
    return this.basicInformationStepFormGroup.get('location') as UntypedFormControl;
  }
  get conclusionDate() {
    return this.basicInformationStepFormGroup.get('conclusionDate') as UntypedFormControl;
  }
  get employmentDateFrom() {
    return this.basicInformationStepFormGroup.get('employmentDateFrom') as UntypedFormControl;
  }
  get employmentDateTo() {
    return this.basicInformationStepFormGroup.get('employmentDateTo') as UntypedFormControl;
  }
  get contractDurationValue() {
    return this.basicInformationStepFormGroup.get('contractDurationValue') as UntypedFormControl;
  }
  get contractDurationUnit() {
    return this.basicInformationStepFormGroup.get('contractDurationUnit') as UntypedFormControl;
  }
  get hasContractDurationValueValidationError(): boolean {
    return this.contractDurationValue.touched
      && (this.contractDurationValue.hasError('min')
        || this.contractDurationValue.hasError('max')
        || this.contractDurationValue.hasError('pattern'));
  }

  get employer() {
    return this.basicInformationStepFormGroup.get('employer') as UntypedFormControl;
  }
  get employerObject() {
    return this.basicInformationStepFormGroup.get('employerObject') as FormControl<EmployerFormModel | string | null>;
  }
  get jobTitle() {
    return this.basicInformationStepFormGroup.get('jobTitle') as UntypedFormControl;
  }
  get responsibilities() {
    return this.basicInformationStepFormGroup.get('responsibilities') as UntypedFormControl;
  }
  get organizationalUnit() {
    return this.basicInformationStepFormGroup.get('organizationalUnit') as UntypedFormControl;
  }
  get company() {
    return this.basicInformationStepFormGroup.get('company') as UntypedFormControl;
  }
  get hasEmploymentConditionsConfirmations() {
    return this.basicInformationStepFormGroup.get('hasEmploymentConditionsConfirmations') as UntypedFormControl;
  }
  get orderItem() {
    return this.basicInformationStepFormGroup.get('orderItem') as UntypedFormControl;
  }

  get recruiter() {
    return this.basicInformationStepFormGroup.get('recruiter') as UntypedFormControl;
  }

  get disableAgreementDateLimit(): boolean {
    return this.authService.hasPermission(Permission.DisableAgreementDateLimit);
  }
  get hasAssignOrderItemPermission(): boolean {
    return this.authService.hasPermission(Permission.AssignOrderToAgreement);
  }

  get isSubmitForLegalizationDisabled(): boolean {
    return !this.company.value || !this.employer.value || !this.employerObject.value;
  }

  get supervisorJobTitle() {
    return this.basicInformationStepFormGroup.get('supervisorJobTitle') as UntypedFormControl;
  }

  public displayValue = (value: DictionaryItem): string | undefined => value?.Name;

  public employmentDateFromFilter = (date: Moment): boolean =>
    (this.disableAgreementDateLimit || date?.isSameOrAfter(moment().subtract(this.maxDaysBefore, 'days').startOf('day'))) &&
    (this.prefetchedAgreement?.IsExtension
      ? date.isSameOrAfter(this.employmentDateFrom.value)
      : !this.employmentDateTo.value || date.isSameOrBefore(this.employmentDateTo.value));

  public employmentDateToFilter = (date: Moment): boolean => {
    if (!this.employmentDateFrom.value) {
      return true;
    }

    if (!date.isSameOrAfter(this.employmentDateFrom.value)) {
      return false;
    }

    const maxDate = this.getMaxEmploymentDateTo();

    return maxDate ? date.isSameOrBefore(maxDate) : true;
  };

  private getMaxEmploymentDateTo(): Moment | null {
    if (!this.employmentDateFrom.value || Number(this.employmentTypeId) !== EmploymentType.EmploymentAgreement) {
      return null;
    }

    if (!this.maxDurationMonths) {
      return null;
    }

    return moment(this.employmentDateFrom.value).add(this.maxDurationMonths, 'months').subtract(this.daysToSubtractFromMaxDate, 'day');
  };

  private clearEmploymentDateToIfExceedsMax(): void {
    const maxDate = this.getMaxEmploymentDateTo();
    if (maxDate && this.employmentDateTo.value && moment(this.employmentDateTo.value).isAfter(maxDate)) {
      this.employmentDateTo.reset(null, { emitEvent: false });
    }
  }

  public checkDays(type: string, event: MatDatepickerInputEvent<Date>): void {
    if (this.employmentTypeId == EmploymentType.TemporaryEmploymentAgreement && this.employmentDateTo.value) {
      if (this.employmentTypeId == EmploymentType.TemporaryEmploymentAgreement && this.employmentDateTo.value) {
        const diff = this.employmentDateTo.value.valueOf() - this.employmentDateFrom.value.valueOf();
        const diffOfDays = Math.ceil(diff / (1000 * 3600 * 24));
        if (diffOfDays > this.permittedNumberOfDays) {
          this.openAlertDialog(Messages.Exceeded540DaysMessage);
        }
      }
    }
  }

  public async onOrganizationalUnitClick(): Promise<void> {
    if (!this.company.value) return;

    const res = await firstValueFrom(this.dialog.open(TreeViewerComponent, this.createDialogConfig()).afterClosed().pipe(first()));

    if (res) {
      this.organizationalUnit.setValue(<DictionaryItem>{ Id: res.id, Name: res.name });
    }
  }

  protected createDialogConfig(): MatDialogConfig<TreeViewerComponentData> {
    return {
      disableClose: false,
      autoFocus: false,
      panelClass: 'standard-dialog',
      data: {
        header: Messages.ChooseOrganizationalUnit,
        emptyTreeMessage: Messages.EmptyOrganizationalUnitTree,
        datasource$: this.listOfOrganizationalUnits$,
      },
    };
  }

  async onPrefetchedAgreementChanges(changes: SimpleChanges): Promise<void> {
    if (!changes.prefetchedAgreement?.currentValue) return;

    this.refreshResponsibilitiesForCurrentEmployer();

    if (this.prefetchedAgreement?.IsExtension) {
      this.turnOnExtendedAgreementMode();
      this.listOfAgreementTypes$ = this.listOfAgreementTypes$.pipe(map((items) => items.filter((item) => item.Id !== AgreementTypeEnum.Trial)));
    }

    if (!!this.prefetchedAgreement?.CompanyId) {
      await this.handleOrganizationalUnits(this.prefetchedAgreement?.CompanyId);
      this.listOfCompanies$.pipe(takeUntil(this.unsubscribe$)).subscribe((companies) => {
        this.handleHasMinimalWageValidation(companies, this.prefetchedAgreement?.CompanyId);
      });
    }

    this.getCurrentSupervisor(this.workerId);
  }

  protected refreshResponsibilitiesForCurrentEmployer(): void {
    this.refreshResponsibilities$.next();
  }

  protected onAgreementTypeChange() {
    const handler = (agreementTypes: AgreementTypeDto[], agreementTypeId: number) => {
      const selectedType = agreementTypes.find((a) => a.Id == agreementTypeId);
      this.canBeConcludedForAnIndefinitePeriod = selectedType?.CanBeConcludedForAnIndefinitePeriod;

      this.maxDurationMonths = selectedType?.MaxDurationMonths ?? null;
      this.toggleEmploymentDateTo(this.canBeConcludedForAnIndefinitePeriod);
      this.clearEmploymentDateToIfExceedsMax();
    };

    combineLatest([this.listOfAgreementTypes$, this.agreementType.valueChanges.pipe(startWith(this.agreementType.value))])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([agreementTypes, agreementTypeId]) => handler(agreementTypes, agreementTypeId));
  }

  protected initializePlannedPeriodControls(): void {
    combineLatest([
      this.listOfAgreementTypes$,
      this.agreementType.valueChanges.pipe(startWith(this.agreementType.value)),
      this.employmentDateFrom.valueChanges.pipe(startWith(this.employmentDateFrom.value)),
      this.employmentDateTo.valueChanges.pipe(startWith(this.employmentDateTo.value))
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([agreementTypes, agreementTypeId, employmentDateFrom, employmentDateTo]) => {
        this.applyPlannedPeriodControlsState(
          agreementTypes,
          agreementTypeId,
          employmentDateFrom,
          employmentDateTo,
        );
      });
  }

  protected recalculatePlannedPeriodState(): void {
    firstValueFrom(this.listOfAgreementTypes$)
      .then((agreementTypes) => {
        this.applyPlannedPeriodControlsState(
          agreementTypes,
          this.agreementType.value,
          this.employmentDateFrom.value,
          this.employmentDateTo.value,
        );
      })
      .catch(() => {
      });
  }

  protected isPlannedPeriodSectionVisibleForDates(employmentDateFrom: Date | null, employmentDateTo: Date | null, maxDurationMonths: number | null): boolean {
    if (!this.isSelectedAgreementTypeTrial || !this.isPlannedPeriodOptionEnabled) {
      return false;
    }

    return isPlannedPeriodWithinThreshold(employmentDateFrom, employmentDateTo, maxDurationMonths);
  }

  protected applyPlannedPeriodControlsState(
    agreementTypes: AgreementTypeDto[] | null,
    agreementTypeId: number | null,
    employmentDateFrom: Date | null,
    employmentDateTo: Date | null,
  ): void {
    const selectedType = agreementTypes?.find((type) => type.Id == agreementTypeId);
    const maxDurationMonths = selectedType?.MaxDurationMonths ?? null;
    this.nextContractDurationValueMin = this.resolveContractDurationLimit(
      selectedType?.MinNextContractDurationMonths,
      this.defaultNextContractDurationValueMin,
    );
    this.nextContractDurationValueMax = this.resolveContractDurationLimit(
      selectedType?.MaxNextContractDurationMonths,
      this.defaultNextContractDurationValueMax,
    );
    this.isSelectedAgreementTypeTrial = !!selectedType?.IsTrialEmployment;
    this.isPlannedPeriodOptionEnabled = this.isSelectedAgreementTypeTrial && !!selectedType?.RequirePlannedPeriodSection;

    const isPlannedPeriodSectionVisible = this.isPlannedPeriodSectionVisibleForDates(
      employmentDateFrom,
      employmentDateTo,
      maxDurationMonths,
    );

    if (isPlannedPeriodSectionVisible) {
      this.contractDurationValue.enable({ emitEvent: false });
      this.contractDurationUnit.enable({ emitEvent: false });
      this.contractDurationValue.setValidators([
        Validators.required,
        Validators.min(this.nextContractDurationValueMin),
        Validators.max(this.nextContractDurationValueMax),
        Validators.pattern('^[0-9]+$'),
      ]);
      this.contractDurationUnit.setValidators([Validators.required]);
    } else {
      this.contractDurationValue.clearValidators();
      this.contractDurationUnit.clearValidators();
      this.contractDurationValue.disable({ emitEvent: false });
      this.contractDurationUnit.disable({ emitEvent: false });
    }

    this.contractDurationValue.updateValueAndValidity({ emitEvent: false });
    this.contractDurationUnit.updateValueAndValidity({ emitEvent: false });
  }

  private resolveContractDurationLimit(value: number | null | undefined, fallback: number): number {
    return typeof value === 'number' && value > 0 ? value : fallback;
  }

  protected toggleEmploymentDateTo(disable: boolean) {
    const options = {
      validators: disable ? [] : [Validators.required],
      disable: disable,
      clear: disable,
    };
    this.setValidators(this.employmentDateTo, options);
  }

  protected onCompanyChange(): void {
    combineLatest([this.listOfCompanies$, this.company.valueChanges])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async ([companies, companyId]) => {
        if (!!companyId) {
          if (this.organizationalUnit) {
            this.organizationalUnit.setValue('');
          }
          if (this.jobTitle) {
            this.jobTitle.setValue('');
          }

          if (this.supervisorJobTitle) {
            this.supervisorJobTitle.reset(null, { emitEvent: false });
            this.supervisorJobTitleFilterCtrl.setValue('');
          }

          this.selectedCompany = companies.find((c) => c.Id === companyId) || null;
          await this.handleOrganizationalUnits(companyId);
          this.handleHasMinimalWageValidation(companies, companyId);
        } else {
          this.organizationalUnit.disable();
        }
      });
  }

  private handleHasMinimalWageValidation(companies: CompanyDto[], companyId: any) {
    if (this.prefetchedAgreement) {
      this.prefetchedAgreement.HasMinimalWageValidation = companies.find((company) => company.Id === companyId)?.HasMinimalWageValidation;
    }
  }

  public onLocationChange(): void {
    this.listOfLocations$ = this.location.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getLocations(value);
        } else {
          return of(null);
        }
      }),
    );
  }

  public onSupervisorChange(): void {
    this.listOfSupervisors$ = this.supervisor.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters && isEmployer(this.employerObject.value) && this.employerObject.value?.Id) {
          return this.supervisorsService.getPotentialSupervisors(this.employerObject.value.Id, value);
        } else {
          return of(null);
        }
      }),
    );
  }

  public onTemporaryWorkplaceChange(): void {
    this.listOfTemporaryWorkplaces$ = this.temporaryWorkplace.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getTemporaryWorkplaces(value);
        } else {
          return of(null);
        }
      }),
    );
  }

  public onJobTitleChange(): void {
    this.listOfJobTitles$ = this.organizationalUnit.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged(),
      switchMap((value: DictionaryItem) => {
        this.jobTitleFilterCtrl.setValue('');
        if (!value || !value.Id) return of([]);

        return this.dictionaryService.getJobTitles(null, value.Id);
      }),
    );

    this.filteredJobTitles$ = combineLatest([
      this.listOfJobTitles$.pipe(startWith([])),
      this.jobTitleFilterCtrl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([items, search]) => {
        if (!search) return items;
        const lowerSearch = (search as string).toLowerCase();
        return items.filter((i) => i.Name.toLowerCase().includes(lowerSearch));
      }),
    );
  }

  public onSupervisorJobTitleChange(): void {
    this.listOfSupervisorJobTitles$ = this.company.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      startWith(this.company.value),
      distinctUntilChanged(),
      switchMap((companyId: number) => {
        if (!companyId || !this.employmentTypeId) {
          this.supervisorJobTitle.disable();
          return of([]);
        }

        return this.dictionaryService.getSupervisorJobTitles(companyId, this.employmentTypeId, '').pipe(
          tap((items) => {
            if (items.length > 0) {
              this.supervisorJobTitle.enable();
            } else {
              this.supervisorJobTitle.disable();
            }
          }),
        );
      }),
    );

    this.filteredSupervisorJobTitles$ = combineLatest([
      this.listOfSupervisorJobTitles$.pipe(startWith([])),
      this.supervisorJobTitleFilterCtrl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([items, search]) => {
        if (!search) return items;
        const lowerSearch = (search as string).toLowerCase();
        return items.filter((i) => i.Name.toLowerCase().includes(lowerSearch));
      }),
    );
  }

  public onEmployerChange(): void {
    const employerId$ = this.employer.valueChanges.pipe(
      startWith(this.employer.value),
      distinctUntilChanged(),
    );

    const refresh$ = this.refreshResponsibilities$.pipe(
      startWith(undefined),
    );

    this.listOfResponsibilities$ = combineLatest([
      employerId$,
      refresh$,
    ]).pipe(
      switchMap(([employerId]) =>
        employerId
          ? this.dictionaryService.getResponsibilities(employerId)
          : of([]),
      ),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

    this.listOfResponsibilities$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((availableResponsibilities) => {
        this.synchronizeSelectedResponsibilities(
          availableResponsibilities,
        );
      });

    this.filteredResponsibilities$ = combineLatest([
      this.listOfResponsibilities$,
      this.filterSubject.pipe(startWith('')),
    ]).pipe(
      map(([responsibilities, filterValue]) =>
        this.filterResponsibilities(
          responsibilities,
          filterValue,
        ),
      ),
    );
  }

  private synchronizeSelectedResponsibilities(
    availableResponsibilities: Responsibility[],
  ): void {
    const employerId = this.employer.value;

    // null podczas inicjalizacji nie może kasować wartości z prefetcha
    if (!employerId) {
      return;
    }

    const selected: Responsibility[] =
      this.responsibilities.value ?? [];

    const availableIds = new Set(
      availableResponsibilities.map(({ Id }) => Id),
    );

    const validSelected = selected.filter(({ Id }) =>
      availableIds.has(Id),
    );

    if (validSelected.length === selected.length) {
      return;
    }

    this.responsibilities.setValue(
      validSelected,
      { emitEvent: false },
    );
  }

  private filterResponsibilities(
    responsibilities: Responsibility[],
    filterValue: string,
  ): Responsibility[] {
    const selected: Responsibility[] =
      this.responsibilities.value ?? [];

    const normalizedFilter = filterValue
      ?.trim()
      .toLowerCase() ?? '';

    const filtered = responsibilities.filter(({ Name }) =>
      Name.toLowerCase().includes(normalizedFilter),
    );

    const filteredIds = new Set(
      filtered.map(({ Id }) => Id),
    );

    const selectedOutsideFilter = selected.filter(
      ({ Id }) => !filteredIds.has(Id),
    );

    return [
      ...filtered,
      ...selectedOutsideFilter,
    ];
  }

  public isConclusionDateAfterEmploymentDateFrom = () =>
    !!this.conclusionDate.value && this.employmentDateFrom.value && this.conclusionDate.value > this.employmentDateFrom.value;

  public initEmployerAutoCompletes(): void {
    this.employmentDateFrom.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => this.isConclusionDateAfterEmploymentDateFrom() && this.openAlertDialog(Messages.ConclusionDateIsAfterEmploymentDateFrom));
    this.conclusionDate.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => this.isConclusionDateAfterEmploymentDateFrom() && this.openAlertDialog(Messages.ConclusionDateIsAfterEmploymentDateFrom));

    this.employmentDateFrom.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((date) => {
      if (date && !this.canBeConcludedForAnIndefinitePeriod) {
        this.employmentDateTo.enable();
        this.clearEmploymentDateToIfExceedsMax();
      } else {
        this.employmentDateTo.reset();
        this.employmentDateTo.disable();
      }
    });

    this.employer.statusChanges.pipe(
      startWith(this.employer.status),
      takeUntil(this.unsubscribe$),
    ).subscribe((status) => {
      if (this.prefetchedAgreement?.IsExtension) {
        this.employerObject.enable({ emitEvent: false });
        return;
      }

      if (status === 'VALID') {
        this.employerObject.enable({ emitEvent: false });
      } else {
        this.employerObject.disable({ emitEvent: false });
      }
    });

    let previousEmployerId: number | null | undefined;
    this.listOfEmployerObjects$ = this.employer.valueChanges.pipe(
      startWith(this.employer.value),
      tap((employerId) => {
        const employerChanged = previousEmployerId !== undefined && previousEmployerId !== employerId;
        previousEmployerId = employerId;

        if (employerChanged && this.employerObject.value) {
          this.employerObject.reset(null, { emitEvent: false });
        }
      }),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
      switchMap((employerId) => {
        if (!employerId) {
          this.isEmployerObjectsLoading = false;
          return of([]);
        }

        const selectedEmployerObjectId = isEmployer(this.employerObject.value) ? this.employerObject.value?.Id : null;

        const loadEmployerObjects = (searchText: string, count: number, includeIds: number[] = []) => {
          this.isEmployerObjectsLoading = true;
          return this.dictionaryService.getEmployerObjectsForNewAgreement(employerId, searchText, count, includeIds).pipe(
            finalize(() => {
              this.isEmployerObjectsLoading = false;
            }),
          );
        };

        return concat(
          loadEmployerObjects('', 50, [selectedEmployerObjectId].filter(Boolean) as number[]),
          this.employerObject.valueChanges.pipe(
            debounceTime(this.timeBetweenInput),
            map((value) => (typeof value === 'string' ? value?.trim() : null)),
            filter((searchText) => !!searchText && searchText.length >= this.minimumInputLetters),
            distinctUntilChanged(),
            switchMap((searchText) => loadEmployerObjects(searchText, 500)),
          ),
        );
      }),
      tap((items) => {
        const currentValue = this.employerObject.value;
        const currentEoId: number | null = isEmployer(currentValue) ? currentValue.Id : null;

        if (currentEoId && isEmployer(currentValue) && !currentValue?.Name) {
          const match = items.find((x) => x.Id === currentEoId);
          if (match) {
            this.employerObject.setValue(match);
          }
          return;
        }

        if (typeof currentValue === 'string') {
          const normalizedCurrentValue = currentValue.trim().toLowerCase();
          const exactMatch = items.find((item) => item.Name?.toLowerCase() === normalizedCurrentValue);
          if (exactMatch) {
            this.employerObject.setValue(exactMatch);
            this.employerObjectSelect?.closePanel();
            return;
          }
        }

        const canAutoHandleSelection = currentValue == null || typeof currentValue === 'string';

        if (items.length === 1 && canAutoHandleSelection) {
          this.employerObject.setValue(items[0]);
          this.employerObjectSelect?.closePanel();
          return;
        }

        if (items.length > 1 && canAutoHandleSelection) {
          this.employerObjectSelectRef?.nativeElement.focus();
          this.employerObjectSelect?.openPanel();
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    combineLatest([this.listOfEmployerObjects$, this.employerObject.valueChanges.pipe(startWith(undefined))])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([employerObjects, employerObject]) => {
        if (!employerObject || !isEmployer(employerObject) || isNaN(employerObject.Id)) {
          this.orderItemActive = false;
          return;
        }

        this.selectedEmployerObject = employerObjects.find((p) => p.Id === employerObject.Id) || null;

        if (!this.selectedEmployerObject) {
          this.orderItemActive = false;
          return;
        }

        if (!this.selectedEmployerObject.Mpk) {
          this.openAlertDialog(Messages.EmployerObjectHasNoMpk);
          this.employerObject.setErrors({ employerObjectHasNoMpk: true });
        } else {
          this.mpk.setValue(this.selectedEmployerObject.Mpk);
        }
      });

    combineLatest([
      this.employer.valueChanges.pipe(startWith(this.employer.value)),
      this.employerObject.valueChanges.pipe(
        startWith(this.employerObject.value),
        filter((value): value is EmployerFormModel => value && isEmployer(value) && !isNaN(value.Id)),
        map((value) => value.Id),
        distinctUntilChanged(),
      ),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(300),
        filter(([employerId, employerObjectId]) => !!employerId && !!employerObjectId),
      )
      .subscribe(([employerId, employerObjectId]) => {
        this.loadOrderItems(employerId, employerObjectId, null);
      });
  }

  public openAlertDialog = (message: string): void => {
    this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
      },
    });
  };

  protected setValidators = (fc: UntypedFormControl, options: { validators: any[]; disable?: boolean; clear?: boolean }) => {
    fc.clearValidators();
    fc.setValidators(options.validators);
    this.toggleDisability(fc, options.disable);
    if (!!options.clear) fc.reset(null, { emitEvent: false });
    fc.updateValueAndValidity({ emitEvent: false });
  };

  private toggleDisability = (fc: UntypedFormControl, disable: boolean) => {
    if (!!disable && fc.enabled) {
      fc.disable();
    }
    if (!disable && fc.disabled) {
      fc.enable();
    }
  };

  protected turnOnExtendedAgreementMode(): void {
    this.company.disable({ emitEvent: false });
    this.employer.disable({ emitEvent: false });
    this.employerObject.enable({ emitEvent: false });
  }

  protected getCurrentSupervisor(workerId: number): void {
    if (!this.supervisorModuleAllowed) {
      return;
    }

    if (!!this.workerId && (!this.prefetchedAgreement || !this.prefetchedAgreement.Id || !this.prefetchedAgreement.PotentialSupervisorId) && !this.supervisor.value?.Id) {
      this.supervisorsService.getDirectSupervisor(workerId).subscribe((supervisor: PotentialSupervisorDto) => {
        this.supervisor.setValue(<PotentialSupervisorDto>{ Id: supervisor.Id, Name: supervisor.Name, NameWithEmail: supervisor.NameWithEmail });
      });
    }
  }

  protected async handleSubscriptionModules(): Promise<void> {
    this.supervisorModuleAllowed = await this.modulePermissionService.isAllowedModule(this.moduleNames.Supervisors, this.workerId);

    if (!this.supervisorModuleAllowed) {
      this.supervisor.clearValidators();
      this.supervisor.setValue(null);
      this.supervisor.updateValueAndValidity();
    }
  }

  private disableIfFormControlHasValue(formControl: UntypedFormControl): void {
    formControl.value && formControl.disable();
  }

  private async handleOrganizationalUnits(companyId: number): Promise<void> {
    this.organizationalUnit.enable();
    this.organizationalUnitsSubject$.next(companyId);
    await firstValueFrom(this.listOfOrganizationalUnits$);
  }

  protected onBasicInformationStepFormGroupChange(): void {
    combineLatest([
      this.company.valueChanges.pipe(startWith(null)),
      this.employmentDateFrom.valueChanges.pipe(startWith(null)),
      this.employmentDateTo.valueChanges.pipe(startWith(null)),
      this.agreementType.valueChanges.pipe(startWith(null)),
      this.employer.valueChanges.pipe(startWith(null)),
      this.orderItem.valueChanges.pipe(startWith(null)),
      this.recruiter.valueChanges.pipe(startWith(null))
    ])
      .pipe(takeUntil(this.unsubscribe$), debounceTime(this.timeBetweenInput), distinctUntilChanged(arrayComparer))
      .subscribe((_) => this.formGroupChange.emit(this.updateBasicInformationStep(this.basicInformationStepFormGroup.getRawValue())));
  }

  protected updateBasicInformationStep(value: any): EmploymentAgreementDto {
    return {
      ...this.prefetchedAgreement,
      CompanyId: value.company,
      EmployerId: value.employer,
      HasEmploymentConditionsConfirmations: value.hasEmploymentConditionsConfirmations,
      EmployerObjectId: value.employerObject?.Id,
      EmployerObjectName: value.employerObject?.Name,
      LocationId: value.location?.Id,
      LocationName: value.location?.Name,
      AgreementTypeId: value.agreementType,
      ConclusionDate: value.conclusionDate,
      EmploymentDateFrom: value.employmentDateFrom,
      EmploymentDateTo: value?.employmentDateTo,
      NewContractDurationValue: value.contractDurationValue,
      NewContractDurationUnit: value.contractDurationUnit,
      Responsibilities: value.responsibilities?.map((res) => res.Id),
      PotentialSupervisorId: value.supervisor?.Id,
      PotentialSupervisorName: value.supervisor?.Name,
      OrderItemId: value.orderItem?.Id,
      OrderItemName: value.orderItem?.Name,
      JobTitleId: value.jobTitle,
      OrganizationalUnitId: value.organizationalUnit?.Id,
      TemporaryWorkplaceId: value.temporaryWorkplace?.Id,
      TemporaryWorkplaceName: typeof value.temporaryWorkplace === 'object' ? value.temporaryWorkplace?.Name : value.temporaryWorkplace,
      RecruiterId: value.recruiter,
      SupervisorJobTitleId: value.supervisorJobTitle,
    };
  }

  protected loadOrderItems(employerId: number, employerObjectId: number, currentOrderItemId: number): void {
    if (!employerId || !employerObjectId) {
      return;
    }

    if (!this.hasAssignOrderItemPermission) {
      return;
    }

    const previousOrderItem = this.orderItem.value;

    this.orderService.getAvailableOrderItems(employerId, employerObjectId).subscribe((orderItems: AvailableOrderItemsDto[]) => {
      const emptyOption: AvailableOrderItemsDto = { Id: null, Name: '' };
      const itemsWithEmpty = [emptyOption, ...orderItems];
      this.listOfAvailableOrderItems$ = of(itemsWithEmpty);
      this.orderItemActive = orderItems.length > 0;

      if (currentOrderItemId) {
        this.orderItem.enable({ emitEvent: false });
        this.orderItem.setValue(itemsWithEmpty.filter((s) => s.Id === currentOrderItemId)[0], { emitEvent: false });
        this.orderItemLoaded = true;
        return;
      }

      if (previousOrderItem && previousOrderItem.Id) {
        const stillAvailable = itemsWithEmpty.find((item) => item.Id === previousOrderItem.Id);
        if (stillAvailable) {
          this.orderItem.enable({ emitEvent: false });
          this.orderItem.setValue(stillAvailable, { emitEvent: false });
          return;
        }
      }

      if (!this.orderItemLoaded) {
        const currentValue = this.orderItem.value;
        if (orderItems.length === 1) {
          this.orderItem.enable({ emitEvent: false });
          this.orderItem.setValue(itemsWithEmpty[1], { emitEvent: false });
        } else if (orderItems.length > 1) {
          this.orderItem.enable({ emitEvent: false });
          if (currentValue && itemsWithEmpty.some((item) => item.Id === currentValue.Id)) {
          } else {
            this.orderItem.setValue(null, { emitEvent: false });
          }
        } else {
          this.orderItem.disable({ emitEvent: false });
          this.orderItem.setValue(null, { emitEvent: false });
        }
      }

      if (this.orderItemLoaded) {
        this.orderItemLoaded = false;
      }
    });
  }

  protected handleKeydown(event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.stopPropagation();
    }
  }

  protected submitForMultipleLegalizations(workersIds: number[]): Observable<number[]> {
    const data = this.basicInformationStepFormGroup.getRawValue();
    const payload: SubmitForMultipleLegalizationsDto = {
      employmentTypeId: this.employmentTypeId,
      agreementTypeId: data.agreementType,
      workersIds: workersIds,
      companyId: data.company,
      employerId: data.employer,
      employerObjectId: data.employerObject?.Id,
      isImported: false,
      locationId: data.location?.Id,
      conclusionDate: data.conclusionDate,
      employmentDateFrom: data.employmentDateFrom,
      employmentDateTo: data.employmentDateTo,
      responsibilities: data.responsibilities?.map((res) => res.Id),
      potentialSupervisorId: data.supervisor?.Id,
      jobTitleId: data.jobTitle,
      organizationalUnitId: data.organizationalUnit?.Id,
      temporaryWorkplaceId: data.temporaryWorkplace?.Id,
      temporaryWorkplaceName: data.temporaryWorkplace ? (typeof data.temporaryWorkplace === 'object' ? data.temporaryWorkplace.Name : data.temporaryWorkplace) : null,
    };

    this.spinner.show();
    const action$ = this.legalizationService.submitForMultipleLegalizations(payload);

    return action$.pipe(
      finalize(() => this.spinner.hide()),
      tap((workersIds: number[]) => {
        this.snackbarService.openSuccessSnackBar(Messages.MultipleLegalizationSubmitted);
      }),
    );
  }

  protected isPermittedToLegalization(): boolean {
    return this.authService.hasPermission(Permission.LegalizationInformationVisibilityToAll)
      || this.authService.hasPermission(Permission.LegalizationInformationVisibilityToEmployerObjects);
  }
}
